/**
 * Redux slice for the Audit Queue page.
 *
 * Follows the same pattern as auditSlice.ts (createAsyncThunk + extraReducers).
 *
 * Thunks call auditModuleService against the real /audit_queue and
 * /audit_config backend endpoints. The pages render under the VLM Audit
 * Workflow tab (/vlmAudit).
 */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LOADING_STATE } from "src/types/constants";
import AuditModuleService from "src/services/auditModuleService";
import { saveWorkspaceResult } from "src/store/auditWorkspaceSlice";

// ----------------------------  Types  ----------------------------
export interface AuditQueueUseCase {
  id: string;
  name: string;
  inter_rater_count: number;
}

export interface AuditQueueTask {
  id: string;
  use_case_id: string;
  status: string;
  assigned_to: string | null;
  priority: number;
  created_at: string;
}

export interface AuditQueueResult {
  id: string;
  task_id: string;
  auditor_id: string;
  status: string;
  decisions: Record<string, { decision: string }>;
}

export interface AuditQueueUser {
  login: string;
  name: string;
  role: string;
  isElevated: boolean;
}

export interface AuditQueueState {
  useCases: AuditQueueUseCase[];
  tasks: AuditQueueTask[];
  results: AuditQueueResult[];
  currentUser: AuditQueueUser;
  getUseCasesLoading: LOADING_STATE;
  getTasksLoading: LOADING_STATE;
  getResultsLoading: LOADING_STATE;
  // set once the first load finishes; a later refresh then keeps showing the old list
  tasksLoaded: boolean;
  resultsLoaded: boolean;
}

const initialState: AuditQueueState = {
  useCases: [],
  tasks: [],
  results: [],
  currentUser: { login: "", name: "", role: "", isElevated: false },
  getUseCasesLoading: "idle",
  getTasksLoading: "idle",
  getResultsLoading: "idle",
  tasksLoaded: false,
  resultsLoaded: false,
};

// ----------------------------  Async Thunks  ----------------------------

export const getAuditQueueUseCases = createAsyncThunk("auditQueue/getUseCases", async () => {
  // Use cases are derived from the active configs stored in DB.
  const response = await AuditModuleService.getUseCaseConfigs();
  return response.configs.map((c) => ({
    id: c.id,
    name: c.name,
    inter_rater_count: c.inter_rater_count,
  }));
});

export const getAuditQueueTasks = createAsyncThunk(
  "auditQueue/getTasks",
  async (_currentAlias: string | undefined) => {
    const response = await AuditModuleService.getTasks();
    return response.tasks;
  },
);

export const getAuditQueueResults = createAsyncThunk(
  "auditQueue/getResults",
  async (args: { taskIds: string[]; currentAlias?: string }) => {
    const response = await AuditModuleService.getResults(args.taskIds);
    return response.results;
  },
);

// ----------------------------  Slice  ----------------------------

const { reducer, actions } = createSlice({
  name: "auditQueueSlice",
  initialState,
  reducers: {
    setCurrentUser: (state, { payload }: { payload: AuditQueueUser }) => {
      state.currentUser = payload;
    },
  },
  extraReducers: (builder) => {
    // Use cases
    builder.addCase(getAuditQueueUseCases.pending, (state) => {
      state.getUseCasesLoading = "pending";
    });
    builder.addCase(getAuditQueueUseCases.fulfilled, (state, { payload }) => {
      state.useCases = payload;
      state.getUseCasesLoading = "fulfilled";
    });
    builder.addCase(getAuditQueueUseCases.rejected, (state) => {
      state.getUseCasesLoading = "rejected";
    });

    // Tasks
    builder.addCase(getAuditQueueTasks.pending, (state) => {
      state.getTasksLoading = "pending";
    });
    builder.addCase(getAuditQueueTasks.fulfilled, (state, { payload }) => {
      state.tasks = payload;
      state.getTasksLoading = "fulfilled";
      state.tasksLoaded = true;
    });
    builder.addCase(getAuditQueueTasks.rejected, (state) => {
      state.getTasksLoading = "rejected";
    });

    // Results
    builder.addCase(getAuditQueueResults.pending, (state) => {
      state.getResultsLoading = "pending";
    });
    builder.addCase(getAuditQueueResults.fulfilled, (state, { payload }) => {
      state.results = payload;
      state.getResultsLoading = "fulfilled";
      state.resultsLoaded = true;
    });
    builder.addCase(getAuditQueueResults.rejected, (state) => {
      state.getResultsLoading = "rejected";
    });

    // A save in the workspace moves the task at once (submit -> Completed, draft ->
    // in progress), before the queue's own refresh comes back.
    builder.addCase(saveWorkspaceResult.fulfilled, (state, { payload }) => {
      state.tasks = state.tasks.map((t) => {
        if (t.id !== payload.task_id) return t;
        if (payload.status === "submitted") return { ...t, status: "completed" };
        return t.status === "completed" || t.status === "expired" ? t : { ...t, status: "draft" };
      });
    });
  },
});

export const { setCurrentUser } = actions;

export default reducer;
